<template>
    <VeeForm ref="form" @submit="saveHorse" v-slot="{ isSubmitting, errors }">
        <div class="horse-edit-main flex flex-column gap-4">
            <h2 class="page-header">{{ $t('message.horseEdit.main.header') }} ({{ editHorseHeader }})</h2>
            <HorseEditMainMainData v-model="internalHorse" :validation-errors="validationErrors" :showErrorMessages="showErrorMessages" :disable-edit="disableEdit"/>
            <HorseEditMainDates ref="dates" v-model="internalHorse" :validation-errors="validationErrors" :disable-edit="disableEdit"/>
            <div class="separator"/>
            <HorseEditOwners v-model="internalHorse" :disabled="disableEdit" :disable-edit="disableEdit"
                             :breeders-error-message="!validateBreeders() && wasValidated"
                             :owners-error-message="!validateOwners() && wasValidated"
                             :required="!isExternal"/>
            <div class="separator"/>
            <HorsePreviewPedigree v-if="disableEdit" :selectedHorse="internalHorse" :depthLasts="5"/>
            <HorseEditMainPedigree v-else v-model="internalHorse"/>
            <div class="separator"/>
            <HorseEditMainStudbooks :imported="!!horse.imported" v-model="internalHorse" :disable-edit="disableEdit"/>
            <div class="separator"/>
            <FoalMarkingData v-model="internalHorse" :systemRole="systemRole" only-marking
                             :showErrorMessages="showErrorMessages" :disable-edit="disableEdit"/>
            <div class="separator"/>
            <HorseEditMainOtherData v-model="internalHorse" :disable-edit="disableEdit"/>
        </div>
    </VeeForm>
</template>

<script>
import HorseEditMainMainData from "@/views/horse/horseEdit/main/components/HorseEditMainMainData.vue";
import HorseEditMainDates from "@/views/horse/horseEdit/main/components/HorseEditMainDates.vue";
import HorseEditOwners from "@/views/horse/horseEdit/main/components/HorseEditOwners.vue";
import HorseEditMainStudbooks from "@/views/horse/horseEdit/main/components/HorseEditMainStudbooks.vue";
import HorseEditMainOtherData from "@/views/horse/horseEdit/main/components/HorseEditMainOtherData.vue";
import HorseEditMainPedigree from "@/views/horse/horseEdit/main/components/HorseEditMainPedigree.vue";
import FoalMarkingData from "@/views/horse/foal/components/FoalMarkingData.vue";
import {SystemRole} from "@/utils/SystemRole";
import HorsePreviewPedigree from "@/views/horse/horseList/components/HorsePreviewPedigree.vue";
import { Form as VeeForm } from 'vee-validate';

export default {
    name: "HorseEditMainView",

    components: {
        HorsePreviewPedigree,
        FoalMarkingData,
        HorseEditMainPedigree,
        HorseEditMainOtherData,
        HorseEditMainStudbooks,
        HorseEditOwners,
        HorseEditMainDates,
        HorseEditMainMainData,
        VeeForm
    },

    props: {
        horse: {
            type: Object,
            required: true
        },
        editHorseHeader: {
            type: String,
            default: ''
        },
        systemRole: {
            type: String,
            default: () => SystemRole.getUserRole()
        },
        validationErrors: {
            type: Array,
            default: () => []
        },
        showErrorMessages: {
            type: Boolean,
            default: false
        },
        validateRequest: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            wasValidated: false,
        }
    },

    emits: ["updateHorse", "validationResult", "save"],

    computed: {
        internalHorse: {
            get() { return this.horse; },
            set(value) { this.$emit("updateHorse", value); },
        },
        disableEdit() {
            return this.systemRole !== SystemRole.THIRD_LEVEL_EDITOR && this.systemRole !== SystemRole.ADMIN;
        },
        isExternal() {
            return this.horse?.horseStatus === 'EXTERNAL'
        }
    },

    watch: {
        // Watch for validation requests from the parent
        validateRequest: {
            immediate: true,
            async handler(newVal, oldVal) {
                if (newVal === true && newVal !== oldVal) {
                    await this.runValidation();
                }
            }
        }
    },

    methods: {
        validateOwners() {
            if (this.horse?.horseStatus === 'EXTERNAL') {
                return true;
            }
            if (!this.horse.owners || !Array.isArray(this.horse.owners)) {
                return false;
            }
            return this.horse.owners.filter(owner => owner !== null).length > 0;
        },
        validateBreeders() {
            if (this.horse?.horseStatus === 'EXTERNAL') {
                return true;
            }
            if (!this.horse.breeders || !Array.isArray(this.horse.breeders)) {
                return false;
            }
            return this.horse.breeders.filter(breeder => breeder !== null).length > 0;
        },

        // Run validation and emit the result to parent
        async runValidation() {
            this.wasValidated = true
            try {
                if (this.validateOwners() === false) {
                    this.$emit('validationResult', { valid: false, errors: { owners: ['At least one owner must be selected'] } });
                    return { valid: false, errors: {} };
                }
                if (this.validateBreeders() === false) {
                    this.$emit('validationResult', { valid: false, errors: { breeders: ['At least one breeders must be selected'] } });
                    return { valid: false, errors: {} };
                }
                if (!this.$refs.dates.validateDateOfControl()) {
                    this.$emit('validationResult', { valid: false, errors: { dateOfControl: ['Date of control cannot be earlier than date of birth'] } });
                    return { valid: false, errors: {} };
                }
                if (!this.$refs.dates.validateDateOfImport()) {
                    this.$emit('validationResult', { valid: false, errors: { dateOfImport: ['Date of import cannot be earlier than date of birth'] } });
                    return { valid: false, errors: {} };
                }
                if (!this.$refs.dates.validateDateOfExport()) {
                    this.$emit('validationResult', { valid: false, errors: { dateOfExport: ['Date of export cannot be earlier than date of birth'] } });
                    return { valid: false, errors: {} };
                }
                if (!this.$refs.dates.validateDateOfDeath()) {
                    this.$emit('validationResult', { valid: false, errors: { dateOfDeath: ['Date of death cannot be earlier than date of birth'] } });
                    return { valid: false, errors: {} };
                }
                if (this.$refs.form) {
                    const { valid, errors } = await this.$refs.form.validate();
                    this.$emit('validationResult', { valid, errors });
                    return { valid, errors };
                } else {
                    this.$emit('validationResult', { valid: false, errors: { form: ['Form reference not found'] } });
                    return { valid: false, errors: {} };
                }
            } catch (error) {
                console.error('Validation error:', error);
                this.$emit('validationResult', { valid: false, errors: { form: ['Validation error occurred'] } });
                return { valid: false, errors: {} };
            }
        },

        async saveHorse() {
            const result = await this.runValidation();
            if (result.valid) {
                this.$emit('save', this.internalHorse);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.horse-edit-main {
    .page-header {
        color: var(--accent-color);
        font-weight: 500;
        font-size: 35px;
        font-family: var(--decor-font-family);
    }
    .separator {
        width: 100%;
        height: 1.5px;
        background-color: var(--separator-color);
    }
    .horse-preview-pedigree {
        .pedigree-buttons {
            @media screen and (max-width: 400px) {
                flex-direction: column;
                align-items: start;
            }
        }
        .horse-pedigree-graph {
            overflow-x: auto;
            .pedigree-horse {
                .horse-information {
                    @media screen and (max-width: 1000px) {
                        width: 20vw;
                    }
                }
            }
        }
    }
    .studbook {
        .studbook-number-inputs {
            @media screen and (max-width: 450px) {
                flex-direction: column;
            }
        }
    }
}
</style>
<style lang="scss">
.horse-edit-main {
    .horse-preview-pedigree {
        .pedigree-buttons {
            @media screen and (max-width: 400px) {
                flex-direction: column;
                align-items: start;
            }
        }
        .horse-pedigree-graph {
            overflow-x: auto;
            .pedigree-horse {
                .horse-information {
                    @media screen and (max-width: 1000px) {
                        width: 20vw;
                    }
                }
            }
        }
    }
    .studbook {
        .studbook-number-inputs {
            @media screen and (max-width: 450px) {
                flex-direction: column;
            }
        }
    }
}
</style>
